import * as React from 'react';

import clsx from 'clsx';

import type { BaseComponentProps } from '@coursera/cds-common';
import { ButtonBase } from '@coursera/cds-core';

import getPillCss, { classes } from 'bundles/unified-common/components/pill/getPillCss';

export type Props = BaseComponentProps<'button'> &
  Pick<HTMLAnchorElement, 'href'> & {
    /**
     * Pill label to display.
     */
    children: React.ReactNode;

    /**
     * Invoked when pill is clicked.
     */
    onClick?: () => void;

    /**
     * The refs to the button element.
     */
    refs?: React.RefObject<HTMLButtonElement | HTMLAnchorElement>[];
  };

const mergeRefs = <T = HTMLElement,>(
  refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>
): React.RefCallback<T> => {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null && typeof ref !== 'string') {
        // eslint-disable-next-line no-param-reassign
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
};

/**
 * Pills are compact, interactive components that enable users navigate content experience.
 */
const Pill = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(function Pill(props, ref) {
  const { children, id: idFromProps, refs, ...buttonProps } = props;

  return (
    <ButtonBase
      ref={mergeRefs([ref, ...(refs ?? [])])}
      classes={{
        root: clsx(classes.root),
      }}
      css={getPillCss}
      {...buttonProps}
      onClick={() => {
        props.onClick?.();
      }}
    >
      {children}
    </ButtonBase>
  );
});

export default Pill;
